import React, { useEffect, useRef, useState } from 'react';

interface Props {
    initialPriceCents: number;
    handlePriceChange: (priceCents: number) => void;
    className?: string;
    hasError?: boolean;
}

// Displays an integer-cents value as US currency (e.g. 1999 -> "$19.99") and
// emits integer cents back on change. Formatting is applied to the initial value
// and on blur only; while the user is typing the raw string is preserved so the
// caret and fast input aren't corrupted.
const PriceInputCents = ({ initialPriceCents, handlePriceChange, className, hasError }: Props) => {
    const centsToDisplayFormat = (cents: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format((cents || 0) / 100);

    const displayFormatToCents = (value: string) => {
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
        return !isNaN(numericValue) ? Math.round(numericValue * 100) : 0;
    };

    const [displayValue, setDisplayValue] = useState(() => centsToDisplayFormat(initialPriceCents));

    // The last cents value this input emitted. Used to ignore the parent echoing
    // it straight back into initialPriceCents — reformatting on that echo is what
    // corrupted fast typing (e.g. "57" -> "$5.01"). We only re-sync from the prop
    // when it differs, i.e. a genuine external change.
    const lastEmittedCents = useRef(initialPriceCents);

    useEffect(() => {
        if (initialPriceCents !== lastEmittedCents.current) {
            lastEmittedCents.current = initialPriceCents;
            setDisplayValue(centsToDisplayFormat(initialPriceCents));
        }
    }, [initialPriceCents]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        setDisplayValue(raw); // keep exactly what the user typed while editing
        const cents = displayFormatToCents(raw);
        lastEmittedCents.current = cents;
        handlePriceChange(cents);
    };

    const handleBlur = () => {
        setDisplayValue(centsToDisplayFormat(lastEmittedCents.current));
    };

    return (
        <input
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${className || ''} ${hasError ? 'outline-red-600' : ''}`}
        />
    );
};

export default PriceInputCents;
